import React, { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';
import './ProductDetailsSlideout.css';
import { Button } from '../Button';
import { StarIcon, ShareIcon } from '../Button/icons';
import { ChatInput } from '../ChatInput';
import { ChatMessage } from '../ChatMessage';
import type { ChatMessageProps } from '../ChatMessage';
import { TalkToSalesModal } from '../TalkToSalesModal';
import type { TalkToSalesFormData } from '../TalkToSalesModal';
import { leadService } from '../../services/leadService';

type MobileChatSnap = 'collapsed' | 'expanded';

const MOBILE_CHAT_COLLAPSED_HEIGHT = 120;
const MOBILE_CHAT_TOP_GAP = 84;
const MOBILE_CHAT_EXPANDED_RATIO = 0.85;
const MOBILE_CHAT_DRAG_THRESHOLD = 6;

interface MobileChatSnapHeights {
  collapsed: number;
  expanded: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const getMobileChatSnapHeights = (viewportHeight: number): MobileChatSnapHeights => {
  const collapsed = MOBILE_CHAT_COLLAPSED_HEIGHT;
  const expandedCap = Math.max(collapsed, Math.floor(viewportHeight - MOBILE_CHAT_TOP_GAP));
  const expanded = clamp(
    Math.floor(viewportHeight * MOBILE_CHAT_EXPANDED_RATIO),
    collapsed,
    expandedCap
  );

  return { collapsed, expanded };
};

const nearestSnap = (
  height: number,
  snapHeights: MobileChatSnapHeights
): MobileChatSnap => {
  const candidates: Array<{ snap: MobileChatSnap; delta: number }> = [
    { snap: 'collapsed', delta: Math.abs(height - snapHeights.collapsed) },
    { snap: 'expanded', delta: Math.abs(height - snapHeights.expanded) },
  ];
  candidates.sort((a, b) => a.delta - b.delta);
  return candidates[0].snap;
};

const isTextInputElement = (element: Element | null): boolean => {
  if (!element) return false;
  if (element instanceof HTMLTextAreaElement) return !element.disabled && !element.readOnly;
  if (element instanceof HTMLInputElement) {
    const nonTextTypes = new Set([
      'button',
      'checkbox',
      'color',
      'file',
      'hidden',
      'image',
      'radio',
      'range',
      'reset',
      'submit',
    ]);

    return !nonTextTypes.has(element.type.toLowerCase()) && !element.disabled && !element.readOnly;
  }
  return element instanceof HTMLElement && element.isContentEditable;
};

export interface ProductDetailsSlideoutProps {
  /** Whether the slideout is open */
  isOpen: boolean;
  /** Handler for closing the slideout */
  onClose: () => void;
  /** Product name to display */
  productName?: string;
  /** Product description */
  description?: string;
  /** URL for the main hero image */
  heroImageUrl?: string;
  /** Array of thumbnail image URLs */
  thumbnailImages?: string[];
  /** Alt text for images */
  imageAlt?: string;
  /** Match percentage (0-100) */
  matchPercentage?: number;
  /** Match confidence description */
  matchDescription?: string;
  /** Whether this is marked as top recommendation */
  isTopRecommendation?: boolean;
  /** Product overview content */
  overview?: string;
  /** Handler for TALK TO SALES button click */
  onTalkToSales?: () => void;
  /** Handler for ASK AI button click */
  onAskAI?: () => void;
  /** Handler for Share button click */
  onShare?: () => void;
  /** Optional CSS class name */
  className?: string;
}

export const ProductDetailsSlideout: React.FC<ProductDetailsSlideoutProps> = ({
  isOpen,
  onClose,
  productName = 'Product',
  description = '',
  heroImageUrl,
  thumbnailImages = [],
  imageAlt,
  matchPercentage,
  matchDescription,
  isTopRecommendation = false,
  overview,
  onTalkToSales,
  onAskAI,
  onShare,
  className = '',
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'other'>('overview');
  const [isChatActive, setIsChatActive] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessageProps[]>([]);
  const [isTalkToSalesModalOpen, setIsTalkToSalesModalOpen] = useState(false);
  const initialViewportHeight =
    typeof window !== 'undefined'
      ? window.innerHeight
      : 844;
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [mobileChatSnapHeights, setMobileChatSnapHeights] = useState<MobileChatSnapHeights>(
    getMobileChatSnapHeights(initialViewportHeight)
  );
  const [mobileChatSnap, setMobileChatSnap] = useState<MobileChatSnap>('collapsed');
  const [mobileChatDrawerHeight, setMobileChatDrawerHeight] = useState(
    getMobileChatSnapHeights(initialViewportHeight).collapsed
  );
  const [isMobileChatDragging, setIsMobileChatDragging] = useState(false);
  const [mobileKeyboardHeight, setMobileKeyboardHeight] = useState(0);
  const [mobileKeyboardOpen, setMobileKeyboardOpen] = useState(false);
  const mobileChatMessagesRef = useRef<HTMLDivElement>(null);
  const desktopChatMessagesRef = useRef<HTMLDivElement>(null);
  const mobileChatDrawerHeightRef = useRef(mobileChatDrawerHeight);
  const mobileChatSnapRef = useRef(mobileChatSnap);
  const mobileChatDragSessionRef = useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
    didDrag: boolean;
  } | null>(null);

  // Handle Ask AI button click
  const handleAskAI = () => {
    setIsChatActive(true);
    if (isMobileViewport) {
      setMobileChatSnap('expanded');
      setMobileChatDrawerHeight(mobileChatSnapHeights.expanded);
    }
    if (onAskAI) {
      onAskAI();
    }
  };

  // Handle Talk to Sales button click
  const handleTalkToSales = () => {
    setIsTalkToSalesModalOpen(true);
    if (onTalkToSales) {
      onTalkToSales();
    }
  };

  // Handle Talk to Sales form submission
  const handleTalkToSalesSubmit = async (formData: TalkToSalesFormData) => {
    try {
      console.log('📝 Submitting Talk to Sales form:', formData);
      
      // Create lead using the lead service
      const lead = await leadService.createLead(formData);
      
      console.log('✅ Lead created successfully:', lead);
      
      // Optionally call the parent onTalkToSales handler with the lead data
      if (onTalkToSales) {
        onTalkToSales();
      }
    } catch (error) {
      console.error('❌ Failed to create lead:', error);
      
      // Re-throw the error so the TalkToSalesModal can handle it
      // The modal will show the error state and allow the user to retry
      throw error;
    }
  };

  // Handle sending a message
  const handleSendMessage = (message: string) => {
    if (isMobileViewport && mobileChatSnap === 'collapsed') {
      setMobileChatSnap('expanded');
      setMobileChatDrawerHeight(mobileChatSnapHeights.expanded);
    }

    // Add user message
    const userMessage: ChatMessageProps = {
      type: 'user-response',
      content: message,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    
    // Add loading message
    const loadingMessage: ChatMessageProps = {
      type: 'loading',
      content: '',
    };
    
    setChatMessages(prev => [...prev, loadingMessage]);
    
    // Simulate AI response after a delay
    setTimeout(() => {
      setChatMessages(prev => 
        prev.filter(msg => msg.type !== 'loading').concat([
          {
            type: 'assistant',
            content: `I'd be happy to help you with questions about the ${productName}. What specific information would you like to know?`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          }
        ])
      );
    }, 1500);
  };

  const snapMobileChatDrawer = useCallback(
    (snap: MobileChatSnap) => {
      setMobileChatSnap(snap);
      setMobileChatDrawerHeight(mobileChatSnapHeights[snap]);
    },
    [mobileChatSnapHeights]
  );

  const cycleMobileChatSnap = useCallback(() => {
    const next: MobileChatSnap = mobileChatSnap === 'collapsed' ? 'expanded' : 'collapsed';
    snapMobileChatDrawer(next);
  }, [mobileChatSnap, snapMobileChatDrawer]);

  const handleMobileChatPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    mobileChatDragSessionRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: mobileChatDrawerHeightRef.current,
      didDrag: false,
    };
  };

  const handleMobileChatPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = mobileChatDragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;

    const deltaY = session.startY - event.clientY;
    if (!session.didDrag && Math.abs(deltaY) < MOBILE_CHAT_DRAG_THRESHOLD) return;

    if (!session.didDrag) {
      session.didDrag = true;
      setIsMobileChatDragging(true);
    }

    const nextHeight = clamp(
      session.startHeight + deltaY,
      mobileChatSnapHeights.collapsed,
      mobileChatSnapHeights.expanded
    );
    setMobileChatDrawerHeight(nextHeight);
  };

  const finalizeMobileChatDrag = (didDrag: boolean) => {
    setIsMobileChatDragging(false);
    if (didDrag) {
      snapMobileChatDrawer(nearestSnap(mobileChatDrawerHeightRef.current, mobileChatSnapHeights));
      return;
    }
    cycleMobileChatSnap();
  };

  const handleMobileChatPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = mobileChatDragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    mobileChatDragSessionRef.current = null;
    finalizeMobileChatDrag(session.didDrag);
  };

  const handleMobileChatPointerCancel = () => {
    const session = mobileChatDragSessionRef.current;
    mobileChatDragSessionRef.current = null;
    if (session?.didDrag) {
      finalizeMobileChatDrag(true);
    }
  };

  // Initialize chat with AI greeting when chat becomes active
  useEffect(() => {
    if (isChatActive && chatMessages.length === 0) {
      const greetingMessage: ChatMessageProps = {
        type: 'assistant',
        content: `I see you're interested in ${productName}, want me to help you with anything?`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setChatMessages([greetingMessage]);
    }
  }, [isChatActive, productName, chatMessages.length]);

  useEffect(() => {
    if (!isOpen) {
      setIsChatActive(false);
      setChatMessages([]);
      setMobileChatSnap('collapsed');
      setMobileChatDrawerHeight(mobileChatSnapHeights.collapsed);
      setMobileKeyboardHeight(0);
      setMobileKeyboardOpen(false);
    }
  }, [isOpen, mobileChatSnapHeights.collapsed]);

  useEffect(() => {
    const updateViewport = () => {
      const viewportHeight = window.innerHeight;
      const nextHeights = getMobileChatSnapHeights(viewportHeight);
      setMobileChatSnapHeights(nextHeights);
      setIsMobileViewport(window.innerWidth < 768);
      setMobileChatDrawerHeight((prev) =>
        mobileChatSnapRef.current === 'expanded'
          ? nextHeights.expanded
          : clamp(prev, nextHeights.collapsed, nextHeights.expanded)
      );
    };

    updateViewport();
    window.addEventListener('resize', updateViewport, { passive: true });
    window.visualViewport?.addEventListener('resize', updateViewport, { passive: true });

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('resize', updateViewport);
    };
  }, []);

  useEffect(() => {
    mobileChatDrawerHeightRef.current = mobileChatDrawerHeight;
  }, [mobileChatDrawerHeight]);

  useEffect(() => {
    mobileChatSnapRef.current = mobileChatSnap;
  }, [mobileChatSnap]);

  useEffect(() => {
    if (desktopChatMessagesRef.current) {
      desktopChatMessagesRef.current.scrollTop = desktopChatMessagesRef.current.scrollHeight;
    }
    if (mobileChatMessagesRef.current) {
      mobileChatMessagesRef.current.scrollTop = mobileChatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => {
    if (!isMobileViewport || !isChatActive) {
      setMobileKeyboardHeight(0);
      setMobileKeyboardOpen(false);
      return;
    }

    let rafId: number | null = null;
    const handleViewport = () => {
      const viewport = window.visualViewport;
      if (!viewport) return;

      const layoutHeight = document.documentElement.clientHeight;
      const offset = Math.max(0, layoutHeight - viewport.height - viewport.offsetTop);
      const resolvedOffset = offset >= 40 ? offset : 0;
      const keyboardVisible =
        isTextInputElement(document.activeElement) && resolvedOffset > 0;

      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setMobileKeyboardHeight(resolvedOffset);
        setMobileKeyboardOpen(keyboardVisible);
      });
    };

    handleViewport();
    window.visualViewport?.addEventListener('resize', handleViewport, { passive: true });
    window.visualViewport?.addEventListener('scroll', handleViewport, { passive: true });
    window.addEventListener('focusin', handleViewport);
    window.addEventListener('focusout', handleViewport);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.visualViewport?.removeEventListener('resize', handleViewport);
      window.visualViewport?.removeEventListener('scroll', handleViewport);
      window.removeEventListener('focusin', handleViewport);
      window.removeEventListener('focusout', handleViewport);
    };
  }, [isChatActive, isMobileViewport]);

  // Handle overlay click to close
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close
  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when slideout is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isMobileChatCollapsed =
    mobileChatDrawerHeight <= mobileChatSnapHeights.collapsed + 8;
  const mobileChatDrawerStyle = {
    '--pdp-chat-height': `${mobileChatDrawerHeight}px`,
    '--pdp-chat-keyboard-offset': `${mobileKeyboardHeight}px`,
  } as CSSProperties;

  return (
    <div 
      className="product-details-slideout__overlay"
      onClick={handleOverlayClick}
    >
      <div
        className={`product-details-slideout ${
          isChatActive && !isMobileViewport ? 'product-details-slideout--chat-active' : ''
        } ${
          isChatActive && isMobileViewport ? 'product-details-slideout--mobile-chat-active' : ''
        } ${className}`}
      >
        {/* Header with product name and close/back button */}
        <div className="product-details-slideout__header">
          <button
            className={`product-details-slideout__close-button ${
              isMobileViewport ? 'product-details-slideout__close-button--back' : ''
            }`}
            onClick={onClose}
            aria-label={isMobileViewport ? 'Go back' : 'Close details'}
          >
            {isMobileViewport ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            )}
          </button>

          <div className="product-details-slideout__title-section">
            <h1 className="product-details-slideout__product-name">{productName}</h1>
            {isTopRecommendation && (
              <div className="product-details-slideout__top-badge">
                <StarIcon className="product-details-slideout__star-icon" />
                <span>Top Recommendation</span>
              </div>
            )}
          </div>
        </div>

        {/* Main content area */}
        <div className="product-details-slideout__content">
          {/* Desktop/Tablet chat interface */}
          {isChatActive && !isMobileViewport && (
            <div className="product-details-slideout__chat">
              <div className="product-details-slideout__chat-header">
                <div className="product-details-slideout__chat-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="product-details-slideout__chat-icon">
                    <path d="m5 7 5 5-5 5"/>
                    <path d="m13 7 5 5-5 5"/>
                  </svg>
                  ASK AI ABOUT THE PRODUCT
                </div>
                <button 
                  className="product-details-slideout__chat-close"
                  onClick={() => setIsChatActive(false)}
                  aria-label="Close chat"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              
              <div className="product-details-slideout__chat-messages" ref={desktopChatMessagesRef}>
                {chatMessages.map((message, index) => (
                  <ChatMessage
                    key={index}
                    type={message.type}
                    content={message.content}
                    timestamp={message.timestamp}
                    questionNumber={message.questionNumber}
                    totalQuestions={message.totalQuestions}
                    avatar={message.avatar}
                    author={message.author}
                  />
                ))}
              </div>
              
              <div className="product-details-slideout__chat-input">
                <ChatInput 
                  onSend={handleSendMessage}
                  placeholder="I'm looking for..."
                />
              </div>
            </div>
          )}

          {/* Product details section */}
          <div className="product-details-slideout__product-content">
            {/* Images section */}
            <div className="product-details-slideout__images">
              {/* Thumbnail stack */}
              {thumbnailImages.length > 0 && (
                <div className="product-details-slideout__thumbnails">
                  {thumbnailImages.map((thumb, index) => (
                    <div key={index} className="product-details-slideout__thumbnail">
                      <img 
                        src={thumb} 
                        alt={`${imageAlt || productName} view ${index + 1}`}
                        className="product-details-slideout__thumbnail-image"
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {/* Hero image */}
              {heroImageUrl && (
                <div className="product-details-slideout__hero-image">
                  <img 
                    src={heroImageUrl} 
                    alt={imageAlt || productName}
                    className="product-details-slideout__hero-img"
                  />
                </div>
              )}
            </div>

            {/* Details section */}
            <div className="product-details-slideout__details">
              {/* Confidence message */}
              {matchPercentage !== undefined && (
                <div className="product-details-slideout__confidence">
                  <div className="product-details-slideout__match-badge">
                    <span className="product-details-slideout__checkmark">✓</span>
                    <span className="product-details-slideout__match-text">
                      {matchPercentage}% match
                    </span>
                  </div>
                  {matchDescription && (
                    <p className="product-details-slideout__match-description">
                      {matchDescription}
                    </p>
                  )}
                </div>
              )}

              {/* Tabs */}
              <div className="product-details-slideout__tabs">
                <button 
                  className={`product-details-slideout__tab ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button 
                  className={`product-details-slideout__tab ${activeTab === 'other' ? 'active' : ''}`}
                  onClick={() => setActiveTab('other')}
                >
                  Other recs
                </button>
              </div>

              {/* Tab content */}
              <div className="product-details-slideout__tab-content">
                {activeTab === 'overview' && (
                  <div className="product-details-slideout__overview">
                    <h2 className="product-details-slideout__overview-title">{productName}</h2>
                    <h3 className="product-details-slideout__overview-subtitle">{description}</h3>
                    {overview && (
                      <p className="product-details-slideout__overview-text">{overview}</p>
                    )}
                  </div>
                )}
                {activeTab === 'other' && (
                  <div className="product-details-slideout__other-recs">
                    <p>Other recommendations coming soon...</p>
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="product-details-slideout__ctas">
                <Button
                  type="primary"
                  size="medium"
                  className="product-details-slideout__cta-talk"
                  onClick={handleTalkToSales}
                >
                  TALK TO SALES
                </Button>
                <Button
                  type="outline"
                  size="medium"
                  className="product-details-slideout__cta-ask"
                  onClick={handleAskAI}
                >
                  ASK AI
                </Button>
                <Button
                  icon={<ShareIcon />}
                  isIconOnly
                  className="product-details-slideout__cta-share"
                  aria-label="Share product"
                  onClick={onShare}
                  size="medium"
                  type="outline"
                >
                  Share
                </Button>
              </div>
            </div>
          </div>
        </div>

        {isChatActive && isMobileViewport && (
          <section
            className={`product-details-slideout__mobile-chat-drawer ${
              isMobileChatCollapsed ? 'is-collapsed' : ''
            } ${mobileKeyboardOpen ? 'keyboard-open' : ''} ${
              isMobileChatDragging ? 'is-dragging' : ''
            }`}
            style={mobileChatDrawerStyle}
          >
            <div className="product-details-slideout__mobile-chat-handle-row">
              <button
                type="button"
                className="product-details-slideout__mobile-chat-handle"
                aria-label="Resize product chat drawer"
                onPointerDown={handleMobileChatPointerDown}
                onPointerMove={handleMobileChatPointerMove}
                onPointerUp={handleMobileChatPointerUp}
                onPointerCancel={handleMobileChatPointerCancel}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  cycleMobileChatSnap();
                }}
              >
                <span className="product-details-slideout__mobile-chat-handle-bar" />
                <span className="product-details-slideout__mobile-chat-title">ASK AI ABOUT THIS PRODUCT</span>
              </button>
              <button
                type="button"
                className="product-details-slideout__mobile-chat-close"
                onClick={() => setIsChatActive(false)}
                aria-label="Close product chat"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className={`product-details-slideout__mobile-chat-body ${isMobileChatCollapsed ? 'is-hidden' : ''}`}>
              <div className="product-details-slideout__mobile-chat-messages" ref={mobileChatMessagesRef}>
                {chatMessages.map((message, index) => (
                  <ChatMessage
                    key={`mobile-${index}`}
                    type={message.type}
                    content={message.content}
                    timestamp={message.timestamp}
                    questionNumber={message.questionNumber}
                    totalQuestions={message.totalQuestions}
                    avatar={message.avatar}
                    author={message.author}
                  />
                ))}
              </div>
            </div>

            <div className="product-details-slideout__mobile-chat-input">
              <ChatInput
                onSend={handleSendMessage}
                placeholder="Ask about this product..."
              />
            </div>
          </section>
        )}

        {/* Talk to Sales Modal */}
        <TalkToSalesModal
          isOpen={isTalkToSalesModalOpen}
          onClose={() => setIsTalkToSalesModalOpen(false)}
          onSubmit={handleTalkToSalesSubmit}
        />
      </div>
    </div>
  );
};
